import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"
import _imports_0 from '../../../public/img/learning/recursive-vs-authoritative-dns/recursive-vs-authoritative-dns-query.jpg'


const _hoisted_1 = { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Authoritative DNS servers generally answer DNS queries only for the specific zones they are authoritative for, while a recursive DNS server can answer any DNS query for any zone. Recursive DNS servers use a process called "),
  /*#__PURE__*/_createElementVNode("strong", null, "recursion"),
  /*#__PURE__*/_createTextVNode(" to retrieve answers from authoritative DNS servers.")
], -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/recursive-vs-authoritative-dns/recursive-vs-authoritative-dns.jpg",
    alt: "Recursive DNS servers are like librarians. They're not an authority on anything, but they know where to find the answers.",
    title: "Recursive DNS servers are like librarians. They're not an authority on anything, but they know where to find the answers.",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, [
    /*#__PURE__*/_createTextVNode("Recursive DNS servers are like librarians. They're not an authority on anything, but they know where to find the answers. By NsLookup.io. Licensed under "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://creativecommons.org/licenses/by/4.0/",
      title: "Creative Commons By 4.0 licence",
      rel: "nofollow"
    }, "CC By 4.0"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("h2", null, "Authoritative DNS servers", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("strong", null, "authorities", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", null, "Each authoritative DNS server may host a single DNS zone or many. There is no hard limit on the number of zones a single server can host. The authoritative DNS servers for a business might host several zones for different domains owned by the company. Cloud-scale DNS providers such as Amazon's Route 53 or Google's Cloud DNS host thousands of zones per server or more!", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", null, "Authoritative DNS servers usually don't answer DNS queries from end-user devices such as phones or laptops. That's where recursive DNS servers come in.", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("h2", null, "Recursive DNS servers", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("p", null, "Unlike authoritative DNS servers, recursive DNS servers usually don't host any zones. Recursive DNS servers answer DNS queries from computers and other devices that need to locate resources on the Internet.", -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", null, "DNS queries may be generated by a web browser running on a laptop, an app running on a phone, or a mail server trying to deliver email. When an application needs to look up a name in the DNS, a DNS query is sent to a recursive DNS server.", -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", null, "The recursive DNS server may be automatically configured by the user's Internet Service Provider (ISP), or it may be part of the cellular carrier's infrastructure, or it may be a publicly available DNS resolver that the user has chosen to use.", -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", null, "For each delegation, the recursive DNS server sends the query to another authoritative DNS server. Each query gets it one step closer to the answer. After following around two to four delegations, the recursive DNS server reaches an authoritative DNS server. The authoritative DNS server returns an answer to the query.", -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The recursive DNS server then "),
  /*#__PURE__*/_createElementVNode("strong", null, "caches"),
  /*#__PURE__*/_createTextVNode(" a copy of the answer and returns it to its client.")
], -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("p", null, "Each time a computer joins a wifi or wired network, one or more recursive DNS servers are set automatically.", -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("p", null, "The user of the machine, be it a laptop, phone, or server, can change the network settings to specify different recursive DNS servers instead of these default servers. One might use a different resolver for privacy, performance, DNS features, or personal preference.", -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Some popular recursive DNS servers, also called "),
  /*#__PURE__*/_createElementVNode("strong", null, "open DNS resolvers"),
  /*#__PURE__*/_createTextVNode(" or "),
  /*#__PURE__*/_createElementVNode("strong", null, "public recursive DNS servers"),
  /*#__PURE__*/_createTextVNode(", and their primary IPv4 DNS addresses:")
], -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "Cloudflare - 1.1.1.1"),
    /*#__PURE__*/_createTextVNode(": One of the fastest open DNS resolvers available.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "Google - 8.8.8.8"),
    /*#__PURE__*/_createTextVNode(": The largest open DNS resolver, handling over a trillion queries per day.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "OpenDNS - 208.67.222.222"),
    /*#__PURE__*/_createTextVNode(": Offers a security product suite called \"Umbrella\" in addition to being an open DNS resolver.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "Quad9 - 9.9.9.9"),
    /*#__PURE__*/_createTextVNode(": A global open DNS resolver operated by a Swiss not-for-profit that aims to improve privacy and cybersecurity.")
  ])
], -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("h2", null, "How does recursion work?", -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Let's put recursive DNS servers and authoritative DNS servers together and look at how "),
  /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
  /*#__PURE__*/_createTextVNode(" would be resolved when a user browses to a website:")
], -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("ol", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("The user's laptop sends a DNS query for "),
    /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
    /*#__PURE__*/_createTextVNode(" to its recursive DNS server.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("The recursive DNS server sends a query for "),
    /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
    /*#__PURE__*/_createTextVNode(" to one of the Internet root servers.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("The root server returns a delegation for "),
    /*#__PURE__*/_createElementVNode("code", null, "org"),
    /*#__PURE__*/_createTextVNode(".")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("The recursive DNS server sends a query for "),
    /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
    /*#__PURE__*/_createTextVNode(" to an authoritative server for "),
    /*#__PURE__*/_createElementVNode("code", null, "org"),
    /*#__PURE__*/_createTextVNode(".")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("The "),
    /*#__PURE__*/_createElementVNode("code", null, "org"),
    /*#__PURE__*/_createTextVNode(" server returns a delegation for "),
    /*#__PURE__*/_createElementVNode("code", null, "example.org"),
    /*#__PURE__*/_createTextVNode(".")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("The recursive DNS server sends a query for "),
    /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
    /*#__PURE__*/_createTextVNode(" to an authoritative server for "),
    /*#__PURE__*/_createElementVNode("code", null, "example.org"),
    /*#__PURE__*/_createTextVNode(".")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("The "),
    /*#__PURE__*/_createElementVNode("code", null, "example.org"),
    /*#__PURE__*/_createTextVNode(" server returns the answer: address records for "),
    /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
    /*#__PURE__*/_createTextVNode(".")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, "The recursive DNS server sends the answer back to the user's laptop.")
], -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("img", {
  class: "full-width",
  src: _imports_0,
  alt: "An example of recursion",
  title: "An example of recursion"
}, null, -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("h2", null, "Caching in the DNS", -1)
const _hoisted_23 = /*#__PURE__*/_createElementVNode("p", null, "In the example above, the recursive DNS server had to send the user's query three times. Each query takes from 10 to 100 milliseconds or more to complete. The recursive DNS server must process each response. This is a lot of work!", -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The DNS was designed from the beginning to reduce the work and time required for recursion through "),
  /*#__PURE__*/_createElementVNode("strong", null, "caching"),
  /*#__PURE__*/_createTextVNode(". Caching is the process of saving a copy of a piece of data for a period of time to make subsequent lookups faster.")
], -1)
const _hoisted_25 = /*#__PURE__*/_createElementVNode("p", null, "Caching is so critical to the DNS that every record in the entire DNS contains a Time-to-Live (TTL) value in seconds. The TTL of each record specifies how long that record may be cached.", -1)
const _hoisted_26 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The recursive DNS server caches the result of each query. If a second end user were to query for "),
  /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
  /*#__PURE__*/_createTextVNode(" within the answer's TTL, then the recursive DNS server would return the cached answer. It would not send any DNS queries to the authoritative servers. Caching serves three very important purposes in the DNS:")
], -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("ol", null, [
  /*#__PURE__*/_createElementVNode("li", null, "It reduces DNS query latency."),
  /*#__PURE__*/_createElementVNode("li", null, "It reduces the number of queries each recursive DNS server has to send, reducing the workload on recursive DNS servers."),
  /*#__PURE__*/_createElementVNode("li", null, "It also reduces the load on each of the authoritative servers.")
], -1)
const _hoisted_28 = /*#__PURE__*/_createElementVNode("p", null, "The DNS client, in this example the user's laptop, will also cache DNS results. Client caching further reduces load on recursive and authoritative DNS servers.", -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("h2", null, "Iterative or recursive?", -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("p", null, "Recursive DNS servers are sometimes called iterative DNS servers.", -1)
const _hoisted_31 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("This is because of the type of DNS queries that recursive DNS server send. Recursive DNS servers do not set the "),
  /*#__PURE__*/_createElementVNode("strong", null, "Recursion Desired"),
  /*#__PURE__*/_createTextVNode(" or RD bit in their queries. This bit is specified in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1034",
    rel: "noopener noreferrer"
  }, "RFC 1034"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_32 = /*#__PURE__*/_createElementVNode("p", null, "By not setting this bit, recursive DNS servers request that recursion not be performed on their behalf. Partly, this allows recursive DNS servers to cache DNS data more effectively.", -1)
const _hoisted_33 = /*#__PURE__*/_createElementVNode("p", null, "Queries that do not have the RD bit set are called iterative queries. And so, recursive DNS servers are sometimes called iterative DNS servers.", -1)
const _hoisted_34 = { class: "mx-auto max-w-3xl divide-y-2 divide-gray-200" }
const _hoisted_35 = /*#__PURE__*/_createElementVNode("h2", { class: "ignore-toc" }, "Related questions", -1)
const _hoisted_36 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_37 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " Can a DNS server be authoritative and recursive at the same time? ", -1)
const _hoisted_38 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_39 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "Yes, but this is uncommon and not recomended.")
], -1)
const _hoisted_40 = /*#__PURE__*/_createElementVNode("p", null, "Most DNS server software can operate in either authoritative or recursive mode. Usually these modes are not exclusive. In earlier times, DNS servers would often serve both roles. They would answer queries authoritatively if they hosted the appropriate zone and perform recursion for other queries.", -1)
const _hoisted_41 = /*#__PURE__*/_createElementVNode("p", null, "This is rare today. The primary problem is that authoritative DNS is critical and recursive DNS is expensive. An attacker might find a way to increase load on a DNS server using expensive recursive queries to such a degree it can no longer answer authoritative queries.", -1)
const _hoisted_42 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_43 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " Will recursive servers always return the same data as authoritative servers? ", -1)
const _hoisted_44 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_45 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "Often, yes. But sometimes not due to caching or filtering.")
], -1)
const _hoisted_46 = /*#__PURE__*/_createElementVNode("p", null, "Some public recursive DNS servers offer features such as child-safe browsing, malware protection, spam blocking, and ad blocking. These features may cause them to purposefully return a DNS error instead of a valid DNS result. In this case, the authoritative DNS server for the zone would return an address or other DNS data, but some recursive DNS servers might return errors to protect their users.", -1)
const _hoisted_47 = /*#__PURE__*/_createElementVNode("p", null, "Caching can also result in differences between recursive and authoritative DNS server results. When DNS data is changed on the authoritative DNS servers there will be a period of time, up to the record's TTL in seconds, where end users may see either the old or the new DNS records. This is considered an acceptable trade-off given the considerable benefits that DNS caching provides.", -1)
const _hoisted_48 = /*#__PURE__*/_createElementVNode("p", null, "DNS administrators who wish to change records and minimize impact often temporarily shorten the TTL a few days before making the change. Or they may ensure that end users will not be impacted during the period where both old and new records may be used.", -1)
const _hoisted_49 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_50 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " How can I tell if a DNS server is authoritative or recursive? ", -1)
const _hoisted_51 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_52 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "By checking if the server returns the AA bit in DNS responses.")
], -1)
const _hoisted_53 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("DNS responses contain a special bit called "),
  /*#__PURE__*/_createElementVNode("strong", null, "Authoritative Answer"),
  /*#__PURE__*/_createTextVNode(" or AA bit. This bit is specified in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1034",
    rel: "noopener noreferrer"
  }, "RFC 1034"),
  /*#__PURE__*/_createTextVNode(". An authoritative DNS server will set the AA bit in responses. The AA bit indicates that it is an authoritative server. A recursive DNS server will not set this bit in responses.")
], -1)
const _hoisted_54 = /*#__PURE__*/_createElementVNode("p", null, "You can see this bit using dig and other tools. In the dig output line that starts with \"flags:\" if you see the string \"aa\" then the AA bit is set and the response is from an authoritative DNS server. Here is a response from one of Google's authoritative DNS servers:", -1)
const _hoisted_55 = /*#__PURE__*/_createElementVNode("pre", null, "dig google.com A @ns1.google.com.\n\n; <<>> DiG 9.10.3 <<>> google.com A @ns1.google.com.\n;; global options: +cmd\n;; Got answer:\n;; ->>HEADER<<- opcode: QUERY, status: NOERROR, id: 14203\n;; flags: qr aa rd; QUERY: 1, ANSWER: 1, AUTHORITY: 0, ADDITIONAL: 1\n;; WARNING: recursion requested but not available\n\n;; QUESTION SECTION:\n;google.com.                    IN      A\n\n;; ANSWER SECTION:\ngoogle.com.             300     IN      A       142.250.217.110", -1)
const _hoisted_56 = /*#__PURE__*/_createElementVNode("p", null, "Querying Google's public DNS resolver, which is not authoritative for the google.com zone, the string \"aa\" is not present next to \"flags\". So this response is not authoritative. The absence of the AA bit indicates that this is a recursive DNS server:", -1)
const _hoisted_57 = /*#__PURE__*/_createElementVNode("pre", null, "dig google.com A @8.8.8.8\n\n; <<>> DiG 9.10.3 <<>> google.com A @8.8.8.8\n;; global options: +cmd\n;; Got answer:\n;; ->>HEADER<<- opcode: QUERY, status: NOERROR, id: 43950\n;; flags: qr rd ra; QUERY: 1, ANSWER: 1, AUTHORITY: 0, ADDITIONAL: 1\n\n;; QUESTION SECTION:\n;google.com.                    IN      A\n\n;; ANSWER SECTION:\ngoogle.com.             133     IN      A       142.250.217.110\n", -1)
const _hoisted_58 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("There are other differences in these responses that also show authoritative versus recursive. The TTLs are different: the TTL will count down in a response that came from the cache of a recursive DNS server, but the TTL from the authoritative DNS server will always be the full TTL value. The authoritative server also indicated that it does not support recursion, so dig printed "),
  /*#__PURE__*/_createElementVNode("code", null, "WARNING: recursion requested but not available"),
  /*#__PURE__*/_createTextVNode(".")
], -1)

import BlogPost from '@/components/layout/BlogPost.vue';

import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { jeff, ruurtjan } from '@/common/authors';
import { learningBreadcrumb } from '@/model/Breadcrumb';
import { BlogMeta } from '@/common/blogMeta';
import Callout from '@/components/layout/CalloutComponent.vue';

const meta: BlogMeta = {
  tag: 'DNS concepts',
  url: '/learning/recursive-vs-authoritative-dns/',
  breadcrumbs: [learningBreadcrumb],
  title: 'Recursive vs Authoritative DNS — What\'s the difference?',
  description: 'There are two types of DNS servers: recursive and authoritative. Authoritative servers host DNS data, while recursive DNS servers perform DNS lookups for users.',
  overviewImage: '/img/learning/recursive-vs-authoritative-dns/recursive-vs-authoritative-dns-small.jpg',
  overviewImageAlt: 'Recursive versus Authoritative DNS',
  overviewDescription: 'There are two types of DNS servers: recursive and authoritative. Authoritative servers host DNS data, while recursive DNS servers perform DNS lookups for users.',
  author: jeff,
  editor: ruurtjan,
  img1024x512: 'https://www.nslookup.io/img/learning/recursive-vs-authoritative-dns/recursive-vs-authoritative-dns-social.jpg',
  img1200x630: 'https://www.nslookup.io/img/learning/recursive-vs-authoritative-dns/recursive-vs-authoritative-dns-social.jpg',
  written: new Date('2022-12-29T10:34:08.492Z'),
  updated: new Date('2023-03-28T12:00:26.745Z'),
};

const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'RecursiveVsAuthoritativeDns',
  setup(__props) {


return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"ImageObject\", \"author\": { \"@type\": \"Person\", \"email\": \"mailto:ruurtjan@nslookup.io\", \"image\": \"https://www.nslookup.io/img/ruurtjan-lg.jpg\", \"jobTitle\": \"CEO\", \"name\": \"Ruurtjan Pul\", \"url\": \"https://www.nslookup.io/about/ruurtjan-pul/\" }, \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": 2023, \"contentUrl\": \"https://www.nslookup.io/img/learning/recursive-vs-authoritative-dns/recursive-vs-authoritative-dns.jpg\", \"license\": \"https://creativecommons.org/licenses/by/4.0/\", \"acquireLicensePage\": \"https://creativecommons.org/licenses/by/4.0/\" } ")
        ]),
        _: 1
      }))
    ])),
    _createVNode(BlogPost, {
      title: meta.title,
      description: meta.description,
      url: `https://www.nslookup.io${meta.url}`,
      breadcrumbs: meta.breadcrumbs,
      img1024x512: meta.img1024x512,
      img1200x630: meta.img1200x630,
      written: meta.written,
      updated: meta.updated,
      author: meta.author,
      editor: meta.editor
    }, {
      default: _withCtx(() => [
        _createElementVNode("strong", _hoisted_1, [
          _createTextVNode("There are two types of DNS servers: recursive and authoritative. Authoritative servers host data for "),
          _createVNode(_component_router_link, { to: "/learning/what-is-a-dns-zone/" }, {
            default: _withCtx(() => [
              _createTextVNode("zones")
            ]),
            _: 1
          }),
          _createTextVNode(". Recursive DNS servers perform DNS lookups for users, collecting DNS data from one or more authoritative DNS servers along the way.")
        ]),
        _hoisted_2,
        _hoisted_3,
        _hoisted_4,
        _createElementVNode("p", null, [
          _createTextVNode("As their name suggests, authoritative DNS servers are "),
          _hoisted_5,
          _createTextVNode(" for a "),
          _createVNode(_component_router_link, { to: "/learning/what-is-a-dns-zone/" }, {
            default: _withCtx(() => [
              _createTextVNode("DNS zone")
            ]),
            _: 1
          }),
          _createTextVNode(". They store and serve all of the DNS data for the domain, usually to the entire Internet.")
        ]),
        _createElementVNode("p", null, [
          _createTextVNode("The data in a DNS zone consists of DNS records that allow clients to access resources for the domain. This will include "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/a/" }, {
            default: _withCtx(() => [
              _createTextVNode("A records")
            ]),
            _: 1
          }),
          _createTextVNode(" and "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/aaaa/" }, {
            default: _withCtx(() => [
              _createTextVNode("AAAA records")
            ]),
            _: 1
          }),
          _createTextVNode(" for websites and servers, "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/mx/" }, {
            default: _withCtx(() => [
              _createTextVNode("MX records")
            ]),
            _: 1
          }),
          _createTextVNode(" to enable email for the domain, and other records. Every DNS zone must also include an "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/soa/" }, {
            default: _withCtx(() => [
              _createTextVNode("SOA record")
            ]),
            _: 1
          }),
          _createTextVNode(" and a set of "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/ns/" }, {
            default: _withCtx(() => [
              _createTextVNode("the NS records")
            ]),
            _: 1
          }),
          _createTextVNode(".")
        ]),
        _createElementVNode("p", null, [
          _createTextVNode("Every DNS zone is hosted on multiple authoritative DNS servers for redundancy. Usually, four servers are used. "),
          _createVNode(_component_router_link, { to: "/learning/zone-delegation/" }, {
            default: _withCtx(() => [
              _createTextVNode("Delegations")
            ]),
            _: 1
          }),
          _createTextVNode(" in the parent zone tell the entire Internet how to find the authoritative servers.")
        ]),
        _hoisted_6,
        _hoisted_7,
        _hoisted_8,
        _hoisted_9,
        _hoisted_10,
        _hoisted_11,
        _createElementVNode("p", null, [
          _createTextVNode("Recursive DNS servers resolve DNS queries by starting at one of the DNS root servers and following a chain of delegations. The root servers are special authoritative DNS servers that host the "),
          _createVNode(_component_router_link, {
            to: "/learning/what-is-the-dns-root-zone/",
            title: "What is the DNS root zone?"
          }, {
            default: _withCtx(() => [
              _createTextVNode("root zone")
            ]),
            _: 1
          }),
          _createTextVNode(". The root zone contains a delegation for every Top Level Domain (TLD).")
        ]),
        _hoisted_12,
        _hoisted_13,
        _createVNode(Callout, {
          icon: "info",
          heading: "h3",
          title: "Selecting a recursive DNS server"
        }, {
          default: _withCtx(() => [
            _hoisted_14,
            _hoisted_15,
            _hoisted_16,
            _hoisted_17
          ]),
          _: 1
        }),
        _hoisted_18,
        _hoisted_19,
        _hoisted_20,
        _hoisted_21,
        _hoisted_22,
        _hoisted_23,
        _hoisted_24,
        _hoisted_25,
        _hoisted_26,
        _hoisted_27,
        _hoisted_28,
        _hoisted_29,
        _hoisted_30,
        _hoisted_31,
        _hoisted_32,
        _hoisted_33,
        _createElementVNode("div", _hoisted_34, [
          _hoisted_35,
          _createElementVNode("div", _hoisted_36, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_37,
                    _createElementVNode("span", _hoisted_38, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_39,
                    _hoisted_40,
                    _hoisted_41
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_42, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_43,
                    _createElementVNode("span", _hoisted_44, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_45,
                    _hoisted_46,
                    _hoisted_47,
                    _hoisted_48
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_49, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_50,
                    _createElementVNode("span", _hoisted_51, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_52,
                    _hoisted_53,
                    _hoisted_54,
                    _hoisted_55,
                    _hoisted_56,
                    _hoisted_57,
                    _hoisted_58
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _: 1
    }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"])
  ], 64))
}
}

})